import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { Filter } from '@vk-hr-tek/core/filter';
import { Calendar } from '@vk-hr-tek/core/calendar';

import {
  NewsData,
  Widget,
  WidgetAccount,
  WidgetCompany,
} from '@app/gen/dashboard';

@injectable()
export class WidgetListMapper {
  constructor(
    private calendar: Calendar,
    private format: FormatService,
  ) {}

  private formatAuthorName(
    firstName: string,
    middleName: string,
    secondName: string,
  ): string {
    return `${secondName} ${firstName[0]}.${
      middleName ? ` ${middleName[0]}.` : ''
    }`;
  }

  processListFilters(filters: Filter[]): Filter[] {
    return filters.map((filter) => {
      if (filter.key === 'account_ids' || filter.key === 'company_ids') {
        return {
          ...filter,
          width: 12,
          fullWidth: true,
        };
      }

      return filter;
    });
  }

  processWidget(
    {
      id,
      type,
      custom_name,
      size,
      is_active,
      description,
      settings: { is_content_filled },
      company_id,
      account_id,
    }: Widget,
    companies: WidgetCompany[],
    accounts: WidgetAccount[],
  ) {
    const companyName =
      companies.find((company) => company.id === company_id)?.name ?? '';

    const accountName =
      accounts.find((account) => account.id === account_id)?.name ?? '';

    return {
      companyName,
      accountName,
      widgetId: id,
      widgetType: type,
      isContentFilled: is_content_filled,
      name: custom_name,
      size,
      active: is_active,
      description,
    };
  }

  processWidgets(
    widgets: Widget[],
    companies: WidgetCompany[],
    accounts: WidgetAccount[],
  ) {
    return widgets.map((widget) => ({
      ...widget,
      companyName:
        companies.find(({ id }) => id === widget.company_id)?.name || '',
      accountName:
        accounts.find(({ id }) => id === widget.account_id)?.name || '',
    }));
  }

  processWidgetWithCompanyId(
    widget: Widget,
    companies: WidgetCompany[],
    accounts: WidgetAccount[],
    companyId: string | undefined,
  ) {
    return {
      ...widget,
      companyName:
        companies.find(({ id }) => id === widget.company_id || id === companyId)
          ?.name || '',
      accountName:
        accounts.find(({ id }) => id === widget.account_id)?.name || '',
    };
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (!this.calendar.isValidDate(date)) return dateString;

    const isoDate = this.calendar.formatISO(
      this.calendar.subMinutes(new Date(dateString), 0),
    );

    return `${this.format.toDate(isoDate)} ${this.format.toTime(isoDate)}`;
  }

  processNewsItem({
    id,
    account,
    author,
    companies,
    title,
    announcement,
    cover_att_id,
    category,
    published_at,
    is_important,
  }: NewsData) {
    return {
      id,
      account,
      companies,
      title,
      category,
      announcement,
      coverId: cover_att_id,
      isImportant: is_important,
      author: this.formatAuthorName(
        author.first_name,
        author.middle_name,
        author.second_name,
      ),
      publishedAt: this.formatDate(published_at),
    };
  }

  processNews(newsData: NewsData[]) {
    return newsData.map((item) => this.processNewsItem(item));
  }
}
